import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderInput, Download, Loader2 } from 'lucide-react';
import { generatePagePdf } from '../../lib/generatePagePdf';
import type { Page } from '../../types';

interface PageRowProps {
  page: Page;
  isMineTab: boolean;
  isTeamTab: boolean;
  onMoveToFolder: (id: string) => void;
  onToggleStatus: (page: Page) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  duplicateIsPending: boolean;
}

export function PageRow({
  page: pg,
  isMineTab,
  isTeamTab,
  onMoveToFolder,
  onToggleStatus,
  onDuplicate,
  onDelete,
  duplicateIsPending,
}: PageRowProps) {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      const ok = await generatePagePdf(pg.id);
      if (!ok) alert('PDF-Export fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-5 hover:bg-indigo-50/30 transition-colors">
      <Link to={`/editor/${pg.id}`} className="flex items-center gap-4 flex-1 min-w-0">
        <div className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{pg.title}</h3>
          <p className="text-sm text-gray-400">
            {isTeamTab && pg.createdByName && (
              <span className="text-gray-500">{pg.createdByName} · </span>
            )}
            {new Date(pg.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
            {pg.slotCount ? ` · ${pg.slotCount} Felder` : ''}
          </p>
        </div>
      </Link>
      <div className="flex items-center gap-2 ml-4 shrink-0">
        {/* Move to folder (mine tab only) */}
        {isMineTab && (
          <button
            onClick={() => onMoveToFolder(pg.id)}
            title="In Ordner verschieben"
            aria-label="In Ordner verschieben"
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <FolderInput className="w-4 h-4" />
          </button>
        )}
        {/* PDF download */}
        <button
          onClick={handlePdfDownload}
          disabled={pdfLoading}
          title="Als PDF herunterladen"
          aria-label="Als PDF herunterladen"
          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
        >
          {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </button>
        {/* Visibility badge */}
        {!isTeamTab && (
          <span
            role="img"
            aria-label={pg.visibility === 'organization' ? 'Einrichtung' : pg.visibility === 'team' ? 'Team' : 'Privat'}
            title={pg.visibility === 'organization' ? 'Einrichtung' : pg.visibility === 'team' ? 'Team' : 'Privat'}
            className={`p-2 rounded-lg inline-flex items-center ${
              pg.visibility === 'organization'
                ? 'text-purple-600'
                : pg.visibility === 'team'
                ? 'text-indigo-600'
                : 'text-gray-400'
            }`}
          >
            {pg.visibility === 'organization' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
            ) : pg.visibility === 'team' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
            )}
          </span>
        )}
        {/* Status badge */}
        {!isTeamTab && (
        <button
          onClick={() => onToggleStatus(pg)}
          title={pg.status === 'draft' ? 'Entwurf — klicken für Fertig' : 'Fertig — klicken für Entwurf'}
          aria-label={pg.status === 'draft' ? 'Entwurf — klicken für Fertig' : 'Fertig — klicken für Entwurf'}
          className={`p-2 rounded-lg cursor-pointer transition-colors ${
            pg.status === 'published'
              ? 'text-emerald-600 hover:bg-emerald-50'
              : 'text-amber-600 hover:bg-amber-50'
          }`}
        >
          {pg.status === 'draft' ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          )}
        </button>
        )}
        {/* Duplicate */}
        <button
          onClick={() => onDuplicate(pg.id)}
          disabled={duplicateIsPending}
          title="Seite duplizieren"
          aria-label="Seite duplizieren"
          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
        </button>
        {/* Delete */}
        {!isTeamTab && (
        <button
          onClick={() => onDelete(pg.id)}
          title="Seite löschen"
          aria-label="Seite löschen"
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
        </button>
        )}
      </div>
    </div>
  );
}
